import React, { useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { documentApi } from '../services/api';
import { PageHeader } from '../components/PageHeader';

export type { PolicyTemplateCard };

type Category = 'all' | 'governance' | 'security' | 'hr' | 'technical' | 'data' | 'operations' | 'compliance';

type PolicyTemplateCard = {
  id: string;
  category: Exclude<Category, 'all'>;
  iconClass: string;
  iconBgClass: string;
  categoryLabel: string;
  title: string;
  description: string;
  sections: string;
  estimate: string;
  standards?: string[];
  hasTemplate?: boolean; // true = a pre-written JSON template file exists
};

const TEMPLATES: PolicyTemplateCard[] = [
  // ── Governance ──────────────────────────────────────────────────────────
  {
    id: 'information-security-policy',
    category: 'governance',
    iconClass: 'ri-shield-star-line',
    iconBgClass: 'np-tone-primary',
    categoryLabel: 'Governance',
    title: 'Information Security Policy (Master)',
    hasTemplate: true,
    description:
      'Top-level policy committing the organisation to information security. Must be signed by the most senior executive and communicated to all staff.',
    sections: '10 sections',
    estimate: '~40 min',
    standards: ['ISO 27001 A.5.1', 'ISO 27001 A.5.2', 'SOC 2 CC1.1'],
  },
  {
    id: 'risk-management-policy',
    category: 'governance',
    iconClass: 'ri-error-warning-line',
    iconBgClass: 'np-tone-danger',
    categoryLabel: 'Governance',
    title: 'Risk Management Policy',
    hasTemplate: true,
    description:
      'Comprehensive framework for identifying, assessing, treating, and monitoring organisational risks with integrated controls.',
    sections: '12 sections',
    estimate: '~45 min',
    standards: ['ISO 27001 Clause 6.1', 'ISO 27001 Clause 8.2'],
  },
  {
    id: 'acceptable-use-policy',
    category: 'governance',
    iconClass: 'ri-profile-line',
    iconBgClass: 'np-tone-warning',
    categoryLabel: 'Governance',
    title: 'Acceptable Use Policy',
    hasTemplate: true,
    description:
      'Define acceptable use of IT resources, email, internet access, and company equipment. Required as part of employment terms.',
    sections: '7 sections',
    estimate: '~25 min',
    standards: ['ISO 27001 A.5.10', 'NIS2 POL-1'],
  },
  {
    id: 'asset-management-policy',
    category: 'governance',
    iconClass: 'ri-database-2-line',
    iconBgClass: 'np-tone-primary',
    categoryLabel: 'Governance',
    title: 'Asset Management Policy',
    description:
      'Define how assets are identified, classified, owned, and maintained throughout their lifecycle including disposal procedures.',
    sections: '8 sections',
    estimate: '~30 min',
    standards: ['ISO 27001 A.5.9', 'ISO 27001 A.5.10'],
  },
  {
    id: 'information-classification-policy',
    category: 'governance',
    iconClass: 'ri-price-tag-3-line',
    iconBgClass: 'np-tone-info',
    categoryLabel: 'Governance',
    title: 'Information Classification & Handling Policy',
    description:
      'Establish classification levels (Public, Internal, Confidential, Restricted) and define handling rules for each level.',
    sections: '8 sections',
    estimate: '~30 min',
    standards: ['ISO 27001 A.5.12', 'SOC 2 CC2.1'],
  },
  {
    id: 'information-transfer-policy',
    category: 'governance',
    iconClass: 'ri-send-plane-line',
    iconBgClass: 'np-tone-info',
    categoryLabel: 'Governance',
    title: 'Information Transfer Policy',
    description: 'Define secure transfer requirements for information shared via email, file sharing, physical media, and third-party systems.',
    sections: '7 sections',
    estimate: '~25 min',
    standards: ['ISO 27001 A.5.14'],
  },
  {
    id: 'change-management-policy',
    category: 'governance',
    iconClass: 'ri-git-merge-line',
    iconBgClass: 'np-tone-primary',
    categoryLabel: 'Governance',
    title: 'Change Management Policy',
    description: 'Govern how changes to IT systems, applications, and infrastructure are requested, reviewed, approved, and tracked.',
    sections: '8 sections',
    estimate: '~30 min',
    standards: ['SOC 2 CC3.4', 'SOC 2 CC8.1', 'NIS2 TECH-6'],
  },
  {
    id: 'ip-protection-policy',
    category: 'governance',
    iconClass: 'ri-copyright-line',
    iconBgClass: 'np-tone-info',
    categoryLabel: 'Governance',
    title: 'Intellectual Property Protection Policy',
    description: 'Define controls to protect intellectual property including software licences, proprietary information, and copyright compliance.',
    sections: '6 sections',
    estimate: '~20 min',
    standards: ['ISO 27001 A.5.32'],
  },
  {
    id: 'records-management-policy',
    category: 'governance',
    iconClass: 'ri-archive-line',
    iconBgClass: 'np-tone-success',
    categoryLabel: 'Governance',
    title: 'Records Management Policy',
    description: 'Define how ISMS records are retained, protected, and disposed of, including a data retention schedule.',
    sections: '7 sections',
    estimate: '~25 min',
    standards: ['ISO 27001 A.5.33', 'ISO 27001 C.7.5.3'],
  },
  {
    id: 'legal-compliance-policy',
    category: 'compliance',
    iconClass: 'ri-scales-3-line',
    iconBgClass: 'np-tone-info',
    categoryLabel: 'Compliance',
    title: 'Legal & Regulatory Compliance Policy',
    description: 'Define how the organisation identifies, tracks, and maintains compliance with applicable laws and regulations.',
    sections: '7 sections',
    estimate: '~25 min',
    standards: ['ISO 27001 A.5.31'],
  },

  // ── HR & People ─────────────────────────────────────────────────────────
  {
    id: 'hr-security-policy',
    category: 'hr',
    iconClass: 'ri-user-settings-line',
    iconBgClass: 'np-tone-success',
    categoryLabel: 'HR & People',
    title: 'HR Security Policy',
    description:
      'Cover pre-employment screening, security terms in contracts, security awareness obligations, and off-boarding including access revocation.',
    sections: '9 sections',
    estimate: '~35 min',
    standards: ['ISO 27001 A.6.1', 'ISO 27001 A.6.2', 'ISO 27001 A.6.3', 'NIS2 HR-1'],
  },
  {
    id: 'remote-working-policy',
    category: 'hr',
    iconClass: 'ri-home-wifi-line',
    iconBgClass: 'np-tone-success',
    categoryLabel: 'HR & People',
    title: 'Remote Working Policy',
    description: 'Define security requirements for staff working outside the office: VPN, screen locking, clean desk, public Wi-Fi, and device encryption.',
    sections: '8 sections',
    estimate: '~25 min',
    standards: ['ISO 27001 A.6.7'],
  },
  {
    id: 'background-screening-policy',
    category: 'hr',
    iconClass: 'ri-user-search-line',
    iconBgClass: 'np-tone-success',
    categoryLabel: 'HR & People',
    title: 'Background Screening Policy',
    description: 'Define requirements for pre-employment background checks, vetting levels by role sensitivity, and periodic re-screening.',
    sections: '6 sections',
    estimate: '~20 min',
    standards: ['ISO 27001 A.6.1', 'NIS2 HR-1'],
  },
  {
    id: 'visitor-access-policy',
    category: 'hr',
    iconClass: 'ri-id-card-line',
    iconBgClass: 'np-tone-success',
    categoryLabel: 'HR & People',
    title: 'Visitor Access Policy',
    description: 'Define procedures for registering, escorting, and monitoring visitors to physical premises and sensitive areas.',
    sections: '5 sections',
    estimate: '~15 min',
    standards: ['ISO 27001 A.7.2', 'NIS2 PHY-3'],
  },

  // ── Security ─────────────────────────────────────────────────────────────
  {
    id: 'physical-security-policy',
    category: 'security',
    iconClass: 'ri-door-lock-line',
    iconBgClass: 'np-tone-danger',
    categoryLabel: 'Security',
    title: 'Physical Security Policy',
    description: 'Define physical security perimeters, entry controls, CCTV requirements, and monitoring for all in-scope sites.',
    sections: '8 sections',
    estimate: '~30 min',
    standards: ['ISO 27001 A.7.1', 'SOC 2 CC6.4', 'NIS2 PHY-1'],
  },
  {
    id: 'clear-desk-policy',
    category: 'security',
    iconClass: 'ri-eye-off-line',
    iconBgClass: 'np-tone-warning',
    categoryLabel: 'Security',
    title: 'Clear Desk & Clear Screen Policy',
    description: 'Define requirements for clearing workstations of sensitive materials and locking screens when unattended.',
    sections: '4 sections',
    estimate: '~15 min',
    standards: ['ISO 27001 A.7.7'],
  },
  {
    id: 'incident-response-procedure',
    category: 'security',
    iconClass: 'ri-alarm-warning-line',
    iconBgClass: 'np-tone-danger',
    categoryLabel: 'Security',
    title: 'Incident Response Procedure',
    hasTemplate: true,
    description:
      'Step-by-step procedures for detecting, responding to, and recovering from security incidents including GDPR 72-hour and NIS2 24-hour notification timelines.',
    sections: '8 sections',
    estimate: '~30 min',
    standards: ['ISO 27001 A.5.24', 'SOC 2 CC7.3', 'SOC 2 CC7.4'],
  },
  {
    id: 'access-control-policy',
    category: 'security',
    iconClass: 'ri-key-2-line',
    iconBgClass: 'np-tone-danger',
    categoryLabel: 'Security',
    title: 'Access Control Policy',
    description:
      'Define access control principles, role-based access, privileged access management, and multi-factor authentication requirements.',
    sections: '10 sections',
    estimate: '~35 min',
    standards: ['ISO 27001 A.5.15', 'SOC 2 CC6.1', 'NIS2 AC-1'],
  },
  {
    id: 'authentication-policy',
    category: 'security',
    iconClass: 'ri-fingerprint-line',
    iconBgClass: 'np-tone-danger',
    categoryLabel: 'Security',
    title: 'Authentication Policy',
    description: 'Define password complexity, MFA requirements, session management, and prohibited authentication methods.',
    sections: '6 sections',
    estimate: '~20 min',
    standards: ['ISO 27001 A.5.17'],
  },
  {
    id: 'vulnerability-management-policy',
    category: 'security',
    iconClass: 'ri-bug-2-line',
    iconBgClass: 'np-tone-danger',
    categoryLabel: 'Security',
    title: 'Vulnerability Management Policy',
    description:
      'Define vulnerability scanning frequency, severity classification, patching SLAs (e.g. Critical: 24h, High: 7 days), and exception handling.',
    sections: '8 sections',
    estimate: '~30 min',
    standards: ['ISO 27001 A.8.8', 'SOC 2 CC7.1', 'NIS2 TECH-4'],
  },
  {
    id: 'patch-management-policy',
    category: 'security',
    iconClass: 'ri-tools-line',
    iconBgClass: 'np-tone-warning',
    categoryLabel: 'Security',
    title: 'Patch Management Policy',
    description:
      'Define patch assessment, prioritisation, testing, and deployment timelines across servers, endpoints, and network devices.',
    sections: '7 sections',
    estimate: '~25 min',
    standards: ['NIS2 TECH-3'],
  },
  {
    id: 'anti-malware-policy',
    category: 'security',
    iconClass: 'ri-virus-line',
    iconBgClass: 'np-tone-warning',
    categoryLabel: 'Security',
    title: 'Anti-Malware Policy',
    description:
      'Define malware protection controls including endpoint AV requirements, scanning schedules, email filtering, and response procedures.',
    sections: '6 sections',
    estimate: '~20 min',
    standards: ['ISO 27001 A.8.7', 'NIS2 TECH-7'],
  },
  {
    id: 'penetration-testing-policy',
    category: 'security',
    iconClass: 'ri-crosshair-2-line',
    iconBgClass: 'np-tone-danger',
    categoryLabel: 'Security',
    title: 'Penetration Testing Policy',
    description:
      'Define requirements for internal and external penetration testing including scope, frequency, methodology, and remediation timelines.',
    sections: '7 sections',
    estimate: '~25 min',
    standards: ['NIS2 LEGAL-2'],
  },

  // ── Technical ────────────────────────────────────────────────────────────
  {
    id: 'endpoint-security-policy',
    category: 'technical',
    iconClass: 'ri-macbook-line',
    iconBgClass: 'np-tone-primary',
    categoryLabel: 'Technical',
    title: 'Endpoint Security Policy',
    description:
      'Security requirements for laptops, desktops, mobile devices, and company hardware covering encryption, patching, and anti-malware.',
    sections: '8 sections',
    estimate: '~30 min',
    standards: ['ISO 27001 A.8.1'],
  },
  {
    id: 'cryptography-policy',
    category: 'technical',
    iconClass: 'ri-lock-2-line',
    iconBgClass: 'np-tone-primary',
    categoryLabel: 'Technical',
    title: 'Cryptography & Key Management Policy',
    description:
      'Define approved encryption algorithms, key lengths, key lifecycle management, and prohibited cryptographic methods.',
    sections: '8 sections',
    estimate: '~30 min',
    standards: ['ISO 27001 A.8.24', 'NIS2 CRYPTO-1', 'NIS2 CRYPTO-2'],
  },
  {
    id: 'network-internet-usage-policy',
    category: 'technical',
    iconClass: 'ri-wifi-line',
    iconBgClass: 'np-tone-primary',
    categoryLabel: 'Technical',
    title: 'Network & Internet Usage Policy',
    description:
      'Define acceptable use of corporate networks, web filtering requirements, VPN policy, and restrictions on personal device connections.',
    sections: '7 sections',
    estimate: '~25 min',
    standards: ['ISO 27001 A.8.23'],
  },
  {
    id: 'configuration-management-procedure',
    category: 'technical',
    iconClass: 'ri-terminal-box-line',
    iconBgClass: 'np-tone-primary',
    categoryLabel: 'Technical',
    title: 'Configuration Management Procedure',
    description:
      'Define how baseline configurations are established, maintained, and changed for servers, endpoints, and network devices.',
    sections: '7 sections',
    estimate: '~25 min',
    standards: ['ISO 27001 A.8.9'],
  },
  {
    id: 'logging-monitoring-policy',
    category: 'technical',
    iconClass: 'ri-pulse-line',
    iconBgClass: 'np-tone-info',
    categoryLabel: 'Technical',
    title: 'Logging & Monitoring Policy',
    description: 'Define what events are logged, log retention periods, monitoring responsibilities, and alerting thresholds.',
    sections: '7 sections',
    estimate: '~25 min',
    standards: ['ISO 27001 A.8.15', 'ISO 27001 A.8.16', 'NIS2 TECH-8'],
  },

  // ── Data ─────────────────────────────────────────────────────────────────
  {
    id: 'data-privacy-policy',
    category: 'data',
    iconClass: 'ri-user-shield-line',
    iconBgClass: 'np-tone-info',
    categoryLabel: 'Data',
    title: 'Data Protection & Privacy Policy',
    hasTemplate: true,
    description:
      'GDPR and privacy-compliant policy for handling personal data, data subject rights, lawful basis for processing, and data transfers.',
    sections: '9 sections',
    estimate: '~35 min',
    standards: ['ISO 27001 A.5.34', 'ISO 27001 A.5.32', 'ISO 27001 A.5.33'],
  },
  {
    id: 'data-retention-disposal-policy',
    category: 'data',
    iconClass: 'ri-timer-line',
    iconBgClass: 'np-tone-warning',
    categoryLabel: 'Data',
    title: 'Data Retention & Secure Disposal Policy',
    description:
      'Define retention periods by data type, secure deletion methods for storage media, and certificate of destruction requirements.',
    sections: '7 sections',
    estimate: '~25 min',
    standards: ['ISO 27001 A.8.10'],
  },
  {
    id: 'data-loss-prevention-policy',
    category: 'data',
    iconClass: 'ri-shield-keyhole-line',
    iconBgClass: 'np-tone-warning',
    categoryLabel: 'Data',
    title: 'Data Loss Prevention (DLP) Policy',
    description: 'Define controls preventing unauthorised data exfiltration via email, USB, cloud storage, or printing.',
    sections: '6 sections',
    estimate: '~20 min',
    standards: ['ISO 27001 A.8.12', 'SOC 2 CC6.7'],
  },
  {
    id: 'media-handling-policy',
    category: 'data',
    iconClass: 'ri-hard-drive-2-line',
    iconBgClass: 'np-tone-danger',
    categoryLabel: 'Data',
    title: 'Media Handling Policy',
    description:
      'Define controls for the management, transport, storage, and disposal of removable media and portable storage devices.',
    sections: '5 sections',
    estimate: '~20 min',
    standards: ['NIS2 ASSET-3'],
  },

  // ── Operations ───────────────────────────────────────────────────────────
  {
    id: 'business-continuity-plan',
    category: 'operations',
    iconClass: 'ri-lifebuoy-line',
    iconBgClass: 'np-tone-success',
    categoryLabel: 'Operations',
    title: 'Business Continuity Policy',
    hasTemplate: true,
    description:
      'Framework for maintaining critical operations during disruptions, including BIA, RTO/RPO targets, and DR procedure references.',
    sections: '11 sections',
    estimate: '~50 min',
    standards: ['ISO 27001 A.5.29', 'ISO 27001 A.5.30'],
  },
  {
    id: 'supplier-security-policy',
    category: 'operations',
    iconClass: 'ri-handshake-line',
    iconBgClass: 'np-tone-success',
    categoryLabel: 'Operations',
    title: 'Supplier Security Policy',
    description:
      'Define security requirements for all suppliers, sub-contractors, and cloud providers including due diligence, contractual clauses, and review cadence.',
    sections: '9 sections',
    estimate: '~35 min',
    standards: ['ISO 27001 A.5.19', 'ISO 27001 A.5.20', 'SOC 2 CC9.1', 'NIS2 SC-5'],
  },
  {
    id: 'secure-development-policy',
    category: 'operations',
    iconClass: 'ri-code-s-slash-line',
    iconBgClass: 'np-tone-success',
    categoryLabel: 'Operations',
    title: 'Secure Development & SDLC Policy',
    description:
      'Define security requirements across the SDLC including secure coding standards, SAST/DAST testing gates, and separation of environments.',
    sections: '10 sections',
    estimate: '~40 min',
    standards: ['ISO 27001 A.5.8', 'ISO 27001 A.8.28', 'NIS2 TECH-5'],
  },

  // ── Compliance ───────────────────────────────────────────────────────────
  {
    id: 'document-control-procedure',
    category: 'compliance',
    iconClass: 'ri-file-settings-line',
    iconBgClass: 'np-tone-info',
    categoryLabel: 'Compliance',
    title: 'Document Control Procedure',
    description: 'Define how ISMS documents are created, reviewed, approved, version-controlled, distributed, and retired.',
    sections: '6 sections',
    estimate: '~20 min',
    standards: ['ISO 27001 A.5.37', 'ISO 27001 C.7.5.1'],
  },
  {
    id: 'internal-audit-procedure',
    category: 'compliance',
    iconClass: 'ri-file-search-line',
    iconBgClass: 'np-tone-info',
    categoryLabel: 'Compliance',
    title: 'Internal Audit Procedure',
    description:
      'Define the audit programme: scope, frequency, auditor independence, reporting format, and findings tracking to resolution.',
    sections: '6 sections',
    estimate: '~20 min',
    standards: ['ISO 27001 Clause 9.2'],
  },
  {
    id: 'compliance-monitoring-procedure',
    category: 'compliance',
    iconClass: 'ri-survey-line',
    iconBgClass: 'np-tone-info',
    categoryLabel: 'Compliance',
    title: 'Compliance Monitoring Procedure',
    description: 'Define how compliance with policies, standards, and legal obligations is verified on an ongoing basis.',
    sections: '5 sections',
    estimate: '~20 min',
    standards: ['ISO 27001 Clause 9.2'],
  },
  {
    id: 'threat-intelligence-procedure',
    category: 'compliance',
    iconClass: 'ri-radar-line',
    iconBgClass: 'np-tone-warning',
    categoryLabel: 'Compliance',
    title: 'Threat Intelligence Procedure',
    description:
      'Define how the organisation collects, analyses, and acts on threat intelligence from NCSC, ISAC feeds, CVE notifications, and vendor bulletins.',
    sections: '6 sections',
    estimate: '~20 min',
    standards: ['ISO 27001 A.5.7'],
  },
];

export const PolicyTemplatesPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const registerIdFromUrl = searchParams.get('registerId') || '';
  const [selectedRegisterId, setSelectedRegisterId] = useState<string>(registerIdFromUrl);
  const registerId = selectedRegisterId;
  const pickerRef = useRef<HTMLDivElement>(null);

  const { data: registers = [] } = useQuery<any[]>({
    queryKey: ['documentRegisters'],
    queryFn: documentApi.getRegisters,
    enabled: !registerIdFromUrl, // only fetch if not already provided via URL
  });

  const [category, setCategory] = useState<Category>('all');
  const [search, setSearch] = useState('');

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return TEMPLATES.filter((t) => {
      if (category !== 'all' && t.category !== category) return false;
      if (!term) return true;
      return (
        t.title.toLowerCase().includes(term) ||
        t.description.toLowerCase().includes(term)
      );
    });
  }, [category, search]);

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-file-copy-2-line"
        title="Policy & Document Templates"
        subtitle={`${TEMPLATES.length} professional policy templates. Click "Generate with AI" to draft one tailored to your organisation.`}
        colour="info"
        actions={
          <button className="np-btn np-btn-ghost" onClick={() => window.history.back()}>
            <i className="ri-arrow-left-line"></i>
            Back
          </button>
        }
      />

      {/* Register picker — only shown when page opened without a registerId (e.g. from sidebar) */}
      {!registerIdFromUrl && (
        <div ref={pickerRef} className="np-banner np-tone-warning mb-4" role="alert">
          <i className="ri-error-warning-line"></i>
          <div className="flex-grow-1">
            <strong>Select a document register</strong> to activate the Use Template buttons.
          </div>
          <select
            className="np-select"
            style={{ maxWidth: 260 }}
            value={selectedRegisterId}
            onChange={(e) => setSelectedRegisterId(e.target.value)}
          >
            <option value="">Choose register…</option>
            {registers.map((r: any) => (
              <option key={r.registerId} value={r.registerId}>
                {r.name || r.registerName || r.registerId}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="np-toolbar">
        <div className="np-pill-toggle-group">
          {(
            [
              { key: 'all', label: 'All' },
              { key: 'governance', label: 'Governance' },
              { key: 'security', label: 'Security' },
              { key: 'hr', label: 'HR & People' },
              { key: 'technical', label: 'Technical' },
              { key: 'data', label: 'Data' },
              { key: 'operations', label: 'Operations' },
              { key: 'compliance', label: 'Compliance' },
            ] as { key: Category; label: string }[]
          ).map(({ key, label }) => (
            <button
              key={key}
              className={`np-pill-toggle ${category === key ? 'active np-tone-primary' : ''}`}
              onClick={() => setCategory(key)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="np-search" style={{ maxWidth: 280 }}>
          <i className="ri-search-line"></i>
          <input
            placeholder="Search templates..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="row">
        {visible.map((t) => {
          const to = registerId
            ? `/documents/new?registerId=${encodeURIComponent(registerId)}&template=${encodeURIComponent(t.id)}`
            : '#';

          return (
            <div key={t.id} className="col-md-6 col-lg-4 mb-4">
              <div className="np-card h-100 d-flex flex-column">
                <div className="np-card-body d-flex flex-column flex-grow-1">
                  <div className={`np-icon-badge ${t.iconBgClass} mb-3`}>
                    <i className={t.iconClass}></i>
                  </div>
                  <span className="np-chip np-tone-primary mb-1">{t.categoryLabel}</span>
                  <h5 className="mt-2 mb-2">{t.title}</h5>
                  <p className="np-sub mb-3 flex-grow-1">{t.description}</p>
                  {t.standards && t.standards.length > 0 && (
                    <div className="mb-3 d-flex flex-wrap gap-1">
                      {t.standards.map((s) => (
                        <span key={s} className="np-chip np-tone-neutral">
                          {s}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="np-sub d-flex justify-content-between align-items-center mb-3">
                    <span>
                      <i className="ri-file-text-line me-1"></i>
                      {t.sections}
                    </span>
                    <span>
                      <i className="ri-time-line me-1"></i>
                      {t.estimate}
                    </span>
                  </div>
                  <div className="d-flex gap-2">
                    <Link
                      to={registerId ? to : '#'}
                      className={`np-btn np-btn-ghost np-btn-sm flex-grow-1 ${!t.hasTemplate ? 'disabled' : ''}`}
                      style={!t.hasTemplate ? { pointerEvents: 'none' } : {}}
                      title={!t.hasTemplate ? 'Template coming soon' : undefined}
                      onClick={(e) => {
                        if (!t.hasTemplate) { e.preventDefault(); return; }
                        if (!registerId) {
                          e.preventDefault();
                          pickerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          pickerRef.current?.querySelector('select')?.focus();
                        }
                      }}
                    >
                      <i className="ri-file-copy-line"></i>
                      Use Template
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        {visible.length === 0 && (
          <div className="col-12">
            <div className="np-card">
              <div className="np-card-body text-center py-5">
                <i className="ri-search-line ri-2x mb-2 d-block" style={{ color: 'var(--bs-secondary-color)' }}></i>
                <p className="np-sub mb-0">No templates match your search.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
